import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { Building, FloppyDisk } from '@phosphor-icons/react';

const businessTypes = [
  { value: 'sole_trader', label: 'Sole Trader' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'limited_company', label: 'Limited Company' },
  { value: 'llc', label: 'Limited Liability Company (LLC)' },
  { value: 'other', label: 'Other' }
];

function BusinessDetailsForm({ businessDetails, onSubmit, loading, onBack }) {
  const [formData, setFormData] = useState({
    businessType: businessDetails?.businessType || '',
    businessNumber: businessDetails?.businessNumber || '',
    vatNumber: businessDetails?.vatNumber || '',
    tradingName: businessDetails?.tradingName || '',
    registeredAddress: {
      street: businessDetails?.registeredAddress?.street || '',
      city: businessDetails?.registeredAddress?.city || '',
      state: businessDetails?.registeredAddress?.state || '',
      postalCode: businessDetails?.registeredAddress?.postalCode || '',
      country: businessDetails?.registeredAddress?.country || 'United Kingdom'
    }
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.businessType) {
      newErrors.businessType = 'Business type is required';
    }

    if (!formData.registeredAddress.street) {
      newErrors['registeredAddress.street'] = 'Street address is required';
    }

    if (!formData.registeredAddress.city) {
      newErrors['registeredAddress.city'] = 'City is required';
    }

    if (!formData.registeredAddress.postalCode) {
      newErrors['registeredAddress.postalCode'] = 'Postal code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <Building size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
        Business Details
      </Typography>
      
      <Typography variant="body2" color="text.secondary" mb={3}>
        Please provide your business registration details. This information helps us verify your business legitimacy.
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Business Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.businessType}>
              <InputLabel>Business Type *</InputLabel>
              <Select
                value={formData.businessType}
                onChange={(e) => handleChange('businessType', e.target.value)}
                label="Business Type *"
              >
                {businessTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.businessType && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.businessType}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Trading Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Trading Name"
              value={formData.tradingName}
              onChange={(e) => handleChange('tradingName', e.target.value)}
              helperText="If different from your registered business name"
              error={!!errors.tradingName}
            />
          </Grid>

          {/* Business Number */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Business Registration Number"
              value={formData.businessNumber}
              onChange={(e) => handleChange('businessNumber', e.target.value)}
              helperText="Company registration number (if applicable)"
              error={!!errors.businessNumber}
            />
          </Grid>

          {/* VAT Number */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="VAT Registration Number"
              value={formData.vatNumber}
              onChange={(e) => handleChange('vatNumber', e.target.value)}
              helperText="VAT number (if applicable)"
              error={!!errors.vatNumber}
            />
          </Grid>

          {/* Registered Address */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Registered Business Address *
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street Address"
              value={formData.registeredAddress.street}
              onChange={(e) => handleChange('registeredAddress.street', e.target.value)}
              required
              error={!!errors['registeredAddress.street']}
              helperText={errors['registeredAddress.street']}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="City"
              value={formData.registeredAddress.city}
              onChange={(e) => handleChange('registeredAddress.city', e.target.value)}
              required
              error={!!errors['registeredAddress.city']}
              helperText={errors['registeredAddress.city']}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="State/County"
              value={formData.registeredAddress.state}
              onChange={(e) => handleChange('registeredAddress.state', e.target.value)}
              error={!!errors['registeredAddress.state']}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Postal Code"
              value={formData.registeredAddress.postalCode}
              onChange={(e) => handleChange('registeredAddress.postalCode', e.target.value)}
              required
              error={!!errors['registeredAddress.postalCode']}
              helperText={errors['registeredAddress.postalCode']}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Country"
              value={formData.registeredAddress.country}
              onChange={(e) => handleChange('registeredAddress.country', e.target.value)}
              error={!!errors['registeredAddress.country']}
            />
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> All information provided will be kept confidential and used solely for verification purposes. 
            We may request additional documentation to verify the information provided.
          </Typography>
        </Alert>

        <Box display="flex" justifyContent="space-between">
          {onBack && (
            <Button
              onClick={onBack}
              size="large"
              sx={{ minWidth: 120 }}
            >
              Back
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={<FloppyDisk size={20} />}
            sx={{ minWidth: 150, ml: onBack ? 'auto' : 0 }}
          >
            {loading ? 'Saving...' : 'Save & Continue'}
          </Button>
        </Box>
      </form>
    </Box>
  );
}

export default BusinessDetailsForm;
